import type {
  DashboardAddressSummary,
  DashboardConstructionSiteSummary,
  DashboardDocumentationStats,
  DashboardPersonSummary,
  DashboardRecentJobReport,
  DashboardSeatUsage,
  DashboardSetup,
  DashboardSnapshot,
  DashboardToday,
  DashboardTodayJob,
  DashboardWeekStats,
} from "@/domain/model/Dashboard";
import type {
  DashboardAddressSummaryResponseDto,
  DashboardConstructionSiteSummaryResponseDto,
  DashboardDocumentationStatsResponseDto,
  DashboardPersonSummaryResponseDto,
  DashboardRecentJobReportResponseDto,
  DashboardSeatUsageResponseDto,
  DashboardSetupResponseDto,
  DashboardSnapshotResponseDto,
  DashboardTodayJobResponseDto,
  DashboardTodayResponseDto,
  DashboardWeekStatsResponseDto,
} from "../dto/DashboardDtos";

export function mapDashboardSnapshotResponseDtoToDomain(
  dto: DashboardSnapshotResponseDto,
): DashboardSnapshot {
  return {
    accountId: dto.accountId,
    generatedAt: toRequiredDate(dto.generatedAt, "generatedAt"),
    businessDate: toRequiredIsoDate(dto.businessDate, "businessDate"),
    timezone: dto.timezone,

    setup: mapSetup(dto.setup),
    hero: {
      state: dto.hero.state,
      onboardingSteps: dto.hero.onboardingSteps.map((step) => ({
        kind: step.kind,
        completed: step.completed,
      })),
      todayPlannedCount: dto.hero.todayPlannedCount,
      todayActiveCount: dto.hero.todayActiveCount,
      todayCompletedCount: dto.hero.todayCompletedCount,
    },
    today: mapToday(dto.today),
    recentReports: dto.recentReports.map(mapRecentReport),
    week: mapWeek(dto.week),
  };
}

function mapSetup(dto: DashboardSetupResponseDto): DashboardSetup {
  return {
    jobCount: dto.jobCount,
    enabledAppUserCount: dto.enabledAppUserCount,
    mobileEventCount: dto.mobileEventCount,
    seatUsage: dto.seatUsage ? mapSeatUsage(dto.seatUsage) : null,
  };
}

function mapSeatUsage(dto: DashboardSeatUsageResponseDto): DashboardSeatUsage {
  return {
    purchasedSeats: dto.purchasedSeats,
    usedSeats: dto.usedSeats,
  };
}

function mapToday(dto: DashboardTodayResponseDto): DashboardToday {
  return {
    date: toRequiredIsoDate(dto.date, "today.date"),
    plannedCount: dto.plannedCount,
    activeCount: dto.activeCount,
    completedCount: dto.completedCount,
    jobs: dto.jobs.map(mapTodayJob),
  };
}

function mapTodayJob(dto: DashboardTodayJobResponseDto): DashboardTodayJob {
  return {
    id: dto.id,
    title: dto.title,
    plannedStartAt: toOptionalDate(dto.plannedStartAt, "plannedStartAt"),
    clientName: dto.clientName,
    constructionSite: mapConstructionSite(dto.constructionSite),
    assignedPerson: dto.assignedPerson
      ? mapPerson(dto.assignedPerson)
      : null,
    state: dto.state,
    lastEventAt: toOptionalDate(dto.lastEventAt, "lastEventAt"),
  };
}

function mapConstructionSite(
  dto: DashboardConstructionSiteSummaryResponseDto,
): DashboardConstructionSiteSummary {
  return {
    label: dto.label,
    address: dto.address ? mapAddress(dto.address) : null,
  };
}

function mapAddress(
  dto: DashboardAddressSummaryResponseDto,
): DashboardAddressSummary {
  return {
    street: dto.street,
    streetNumber: dto.streetNumber,
    zip: dto.zip,
    city: dto.city,
    country: dto.country,
  };
}

function mapPerson(dto: DashboardPersonSummaryResponseDto): DashboardPersonSummary {
  return {
    id: dto.id,
    displayName: dto.displayName,
  };
}

function mapRecentReport(
  dto: DashboardRecentJobReportResponseDto,
): DashboardRecentJobReport {
  return {
    jobId: dto.jobId,
    title: dto.title,
    clientName: dto.clientName,
    constructionSiteLabel: dto.constructionSiteLabel,
    completedAt: toRequiredDate(dto.completedAt, "completedAt"),
    trackedDurationMs: dto.trackedDurationMs,
    documentation: mapDocumentation(dto.documentation),
  };
}

function mapWeek(dto: DashboardWeekStatsResponseDto): DashboardWeekStats {
  return {
    periodStart: toRequiredIsoDate(dto.periodStart, "periodStart"),
    periodEndExclusive: toRequiredIsoDate(
      dto.periodEndExclusive,
      "periodEndExclusive",
    ),
    completedJobs: dto.completedJobs,
    trackedDurationMs: dto.trackedDurationMs,
    documentation: mapDocumentation(dto.documentation),
  };
}

function mapDocumentation(
  dto: DashboardDocumentationStatsResponseDto,
): DashboardDocumentationStats {
  return {
    photoCount: dto.photoCount,
    noteCount: dto.noteCount,
    signatureCount: dto.signatureCount,
  };
}

function toRequiredDate(value: string, fieldName: string): Date {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid dashboard date field: ${fieldName}`);
  }

  return date;
}

function toOptionalDate(
  value: string | null,
  fieldName: string,
): Date | null {
  if (value == null) return null;
  return toRequiredDate(value, fieldName);
}

function toRequiredIsoDate(value: string, fieldName: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error(`Invalid dashboard local date field: ${fieldName}`);
  }

  return value;
}
